import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Globe, LogIn } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";

export default function PublicNavbar({ variant }) {
  const home = variant === "home";
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { lang, setLang, t } = useI18n();
  const links = [
    { to: "/", label: t("nav_home") },
    { to: "/about", label: t("nav_about") },
    { to: "/programs", label: t("nav_programs") },
    { to: "/gallery", label: t("nav_gallery") },
    { to: "/internships", label: t("nav_internships") },
    { to: "/test-series", label: t("nav_tests") },
    { to: "/contact", label: t("nav_contact") },
  ];
  const dashPath =
    user && user.role === "admin" ? "/admin"
      : user && user.role === "volunteer" ? "/volunteer"
      : user ? "/student" : null;

  return (
    <header className={`sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200 ${home ? "shadow-sm" : ""}`}>
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between ${home ? "h-[72px]" : "h-16"}`}>
        <Link to="/" data-testid="brand-home-link">
          <Logo size={home ? 52 : 40} />
        </Link>
        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `text-sm font-semibold transition-colors ${
                  isActive
                    ? `text-brand ${home ? "border-b-2 border-brand pb-1" : ""}`
                    : "text-slate-600 hover:text-brand"
                }`
              }
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
            {!home && <button
              onClick={() => setLang(lang === "en" ? "hi" : "en")}
              className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-700 hover:text-brand px-3 py-2 rounded-full hover:bg-slate-100"
              data-testid="lang-toggle"
              aria-label="Toggle language"
            >
              <Globe size={14} /> {lang === "en" ? "हिंदी" : "EN"}
            </button>}
            <Link
                to="/donate"
                className={`${home ? "bg-orange-500 hover:bg-orange-600 px-7 py-3 shadow-lg shadow-orange-500/25" : "bg-[#F59E0B] hover:bg-[#D97706] px-6 py-2.5"} text-white font-bold text-sm rounded-full transition-colors inline-flex items-center gap-1.5`}
                data-testid="nav-donate-button"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
                {t("nav_donate")}
            </Link>
          {dashPath ? (
            <Link
              to={dashPath}
              className="bg-brand hover:bg-[#172b66] text-white font-semibold text-sm rounded-full px-5 py-2.5 transition-colors"
              data-testid="nav-dashboard-link"
            >
              {t("nav_dashboard")}
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                className={home ? "text-sm font-bold text-[#0b1637] border-2 border-[#0b1637] rounded-full px-7 py-2.5 hover:bg-[#0b1637] hover:text-white transition-colors" : "text-sm font-semibold text-slate-700 hover:text-brand"}
                data-testid="nav-login-link"
              >
                {t("nav_login")}
              </Link>
            </>
          )}
        </div>
        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 rounded-md hover:bg-slate-100"
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white">
          <div className="px-4 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm font-semibold ${
                    isActive ? "bg-blue-50 text-brand" : "text-slate-700"
                  }`
                }
                data-testid={`mobile-nav-${l.label.toLowerCase()}-link`}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="border-t pt-3 mt-2 flex flex-col gap-2">
              {dashPath ? (
                <Link
                  to={dashPath}
                  onClick={() => setOpen(false)}
                  className="bg-brand text-white font-semibold text-sm rounded-full px-5 py-2.5 text-center"
                >
                  My Dashboard
                </Link>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold flex items-center gap-2">
                    <LogIn size={16} /> Login
                  </Link>
                  <Link to="/register" onClick={() => setOpen(false)} className="bg-brand text-white font-semibold text-sm rounded-full px-5 py-2.5 text-center">
                    Join Us
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
